import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import Select, { StylesConfig } from 'react-select';
import { UseFormMethods } from 'react-hook-form';
import useFetch from 'use-http';
import { ODataOptions, ODataFilter, ODataFilterOperators } from 'src/util/ODataBuilder';

interface IGLAccountOption {
    value: string; // account number
    label: string; // account description
}

interface IGLAccountSelectProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'setValue'>> {
    name: string;
    defaultAccountNumber?: string;
    style?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    // setValue on a hidden/uncontrolled input only feeds RHF's own isDirty bookkeeping reliably -
    // callers that need the actual selected account number at submit time (not through
    // getValues()) should track it themselves via this callback instead.
    onAccountNumberChange?: (accountNumber: string) => void;
}

// Debounced search-as-you-type GL Account picker. A native <select> (RHFSelect) can't take typed
// input at all, so this uses react-select instead - typing directly into the control drives the
// debounced search against DLCGL.dbo.COA (via the existing /v1.0/Accounting/ledger/accounts
// endpoint, filtered on ACCT_DSCR). The account NUMBER is the value RHF tracks (via a hidden,
// registered input kept in sync through setValue); the description is just the display label.
// Only shows the "Search GL Accounts..." placeholder when nothing is assigned yet - an already-
// assigned account displays as its own value, not as if the field were empty.
const GLAccountSelect = (props: IGLAccountSelectProps) => {
    const { get, response } = useFetch();
    const [options, setOptions] = useState<IGLAccountOption[]>(
        props.defaultAccountNumber ? [{ value: props.defaultAccountNumber, label: props.defaultAccountNumber }] : []
    );
    const [selectedAccountNumber, setSelectedAccountNumber] = useState<string | undefined>(props.defaultAccountNumber);

    // These rows load asynchronously, so this component can mount before the real
    // defaultAccountNumber is known - re-sync if the prop arrives/changes after mount, rather than
    // locking in whatever was there (or wasn't) at the initial render.
    useEffect(() => {
        setSelectedAccountNumber(props.defaultAccountNumber);
        if (props.defaultAccountNumber) {
            setOptions((current) => (current.some((o) => o.value === props.defaultAccountNumber)
                ? current
                : [{ value: props.defaultAccountNumber, label: props.defaultAccountNumber }, ...current]));
        }
    }, [props.defaultAccountNumber]);

    const searchAccounts = async (term: string) => {
        const searchOptions = new ODataOptions();
        searchOptions.addPaging(1, 50);
        if (term) {
            const searchField = !isNaN(Number(term)) ? 'ACCT_NO' : 'ACCT_DSCR';
            searchOptions.addFilter(new ODataFilter(searchField, ODataFilterOperators.Contains, term));
        }

        const result = await get(`/v1.0/Accounting/ledger/accounts${searchOptions.toString()}`);

        if (response.ok && result?.items) {
            setOptions(result.items.map((a: { accountNumber: string; description: string }) => ({
                value: a.accountNumber,
                label: a.description,
            })));
        }
    };

    const debouncedSearch = useRef(_.debounce((term: string) => searchAccounts(term), 800)).current;

    const handleInputChange = (value: string, meta: { action: string }) => {
        if (meta.action === 'input-change') {
            debouncedSearch(value);
        }
        return value;
    };

    const handleChange = (option: IGLAccountOption | null) => {
        setSelectedAccountNumber(option?.value);
        props.setValue?.(props.name, option?.value ?? '', { shouldDirty: true });
        props.onAccountNumberChange?.(option?.value ?? '');
    };

    const selectedOption = options.find((o) => o.value === selectedAccountNumber)
        ?? (selectedAccountNumber ? { value: selectedAccountNumber, label: selectedAccountNumber } : null);

    // Matches the .field class every other RHFInputBox/RHFSelect in this app renders with:
    // bottom border only, grey, no border-radius - not react-select's default rounded full border.
    const customStyles: StylesConfig<IGLAccountOption, false> = {
        control: (provided) => ({
            ...provided,
            minHeight: '30px',
            height: '30px',
            fontSize: '9px',
            border: 'none',
            borderRadius: 0,
            borderBottom: '1px solid grey',
            boxShadow: 'none',
            backgroundColor: 'white',
        }),
        valueContainer: (provided) => ({ ...provided, padding: '0 4px', height: '30px' }),
        input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
        indicatorsContainer: (provided) => ({ ...provided, height: '30px' }),
        dropdownIndicator: (provided) => ({ ...provided, padding: '0 2px' }),
        clearIndicator: (provided) => ({ ...provided, padding: '0 2px' }),
    };

    // The dropdown list still shows the description (however the account was found), but the
    // collapsed field always shows the account NUMBER, regardless of whether it was searched by
    // ACCT_DSCR or ACCT_NO.
    const formatOptionLabel = (option: IGLAccountOption, labelMeta: { context: 'menu' | 'value' }) => (
        labelMeta.context === 'value' ? option.value : option.label
    );

    return (
        <div style={props.bodyStyle ?? { width: '100%' }}>
            <input type="hidden" name={props.name} ref={props.register} defaultValue={props.defaultAccountNumber} />
            <Select
                isClearable={true}
                isSearchable={true}
                options={options}
                value={selectedOption}
                onInputChange={handleInputChange}
                onChange={handleChange}
                formatOptionLabel={formatOptionLabel}
                placeholder="Search GL Accounts..."
                styles={customStyles}
            />
        </div>
    );
};

export default GLAccountSelect;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/components/GLAccountSelect.tsx